import {
  LARGE_MOBILE,
  LOGIN_PATH,
  REGISTER_PATH,
  REGISTER_VERIFY_EMAIL_PATH,
  RESET_PASSWORD_PATH,
} from "common/constants";
import LoginPage from "features/auth/LoginPage";
import React, { useEffect } from "react";
import RegisterForm from "features/auth/RegisterForm";
import ResetPassword from "features/auth/ResetPassword";
import styled, { css, useTheme } from "styled-components";
import useQuery from "utils/hooks/useQuery";
import { localStorageService } from "utils/localStorageService";
import Layout from "antd/es/layout";
import { useDispatch } from "react-redux";
import cornixLogo from "images/svg_logo.svg";
import HideShowTransition from "common/Animations/HideShowTransition";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import VerifyEmail from "features/auth/VerifyEmail";
import useMedia from "utils/hooks/useMedia";
import SecuritySection from "features/auth/SecuritySection";
import { sharedLib } from "@shared/services/shared.service.ts";
import cornixWhiteLogo from "images/svg_white_logo.svg";

const Auth = () => {
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const appTheme = useTheme();
  const dispatch = useDispatch();
  const query = useQuery();
  const uuid = query.get("uuid");

  useEffect(() => {
    dispatch(sharedLib.slices.common.actions.setIsAuth(true));
    return () => {
      dispatch(sharedLib.slices.common.actions.setIsAuth(false));
    };
  }, []);

  const PATH_TO_COMPONENT: Record<string, JSX.Element> = {
    [LOGIN_PATH]: <LoginPage />,
    [RESET_PASSWORD_PATH]: <ResetPassword />,
    [REGISTER_VERIFY_EMAIL_PATH]: <VerifyEmail />,
    [REGISTER_PATH]: <RegisterForm />,
  };
  const paths = Object.keys(PATH_TO_COMPONENT);
  const components = Object.values(PATH_TO_COMPONENT);
  const [componentIndex] = useRoutesMatch(paths) || [0];

  const isLoginOrRegister = !!useRoutesMatch([LOGIN_PATH, REGISTER_PATH]);

  useEffect(() => {
    if (uuid && isLoginOrRegister) {
      localStorageService.setUuid(uuid);
    }
  }, [uuid, isLoginOrRegister]);

  const logo = (
    <StyledLogoImage
      src={appTheme.isDarkMode ? cornixWhiteLogo : cornixLogo}
      alt="logo"
      $isMobile={isMobile}
    />
  );

  const footer = (
    <FooterWrapper>
      <span
        style={{ color: appTheme.colors.white, padding: isMobile ? 24 : 0 }}
      >
        {`© 2018 - ${new Date().getFullYear()} Cornix.io. All rights reserved`}
      </span>
    </FooterWrapper>
  );

  return (
    <Wrapper>
      {!isMobile && <HeaderWrapper>{logo}</HeaderWrapper>}
      <Content>
        <ComponentWrapper>
          <ComponentInnerWrapper>
            {isMobile && logo}
            <HideShowTransition
              components={components}
              stepOrTab={componentIndex}
              style={{
                position: "relative",
                width: "100%",
                paddingBottom: isLoginOrRegister ? 32 : 70,
              }}
            />
          </ComponentInnerWrapper>
          {isLoginOrRegister && <SecuritySection />}
          {isMobile && <MobileFooter>{footer}</MobileFooter>}
        </ComponentWrapper>
      </Content>
      {!isMobile && <LayoutFooter>{footer}</LayoutFooter>}
    </Wrapper>
  );
};

export default Auth;

const StyledLogoImage = styled.img<{ $isMobile?: boolean }>`
  ${(p) =>
    p.$isMobile
      ? `
    margin: 12px 0;
    height: 40px;
    align-self: flex-start;
    `
      : `
    position: absolute;
    left: 20px;
    top: 16px;
    height: 60px;
  `};
`;
const Wrapper = styled(Layout)`
  height: 100vh;
  width: 100vw;
  display: flex;
  background: ${(p) => p.theme.colors.bgColor};
`;
const Content = styled(Layout.Content)`
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 40px 40px 0 40px;
  @media (max-width: ${LARGE_MOBILE}) {
    padding: 0;
    flex-direction: column;
  }
`;
const ComponentWrapper = styled.div`
  position: relative;
  margin: auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: ${LARGE_MOBILE}) {
    height: 100%;
  }
`;
const ComponentInnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 480px;
  @media (max-width: ${LARGE_MOBILE}) {
    padding: 0 20px;
  }
`;
const HeaderWrapper = styled.div`
  width: 100%;
  z-index: 1;
`;
const FooterWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
`;
const footerBgStyle = css`
  background: ${(p) => p.theme.colors.layoutFooterColor};
  width: 100%;
`;
const LayoutFooter = styled(Layout.Footer)`
  ${footerBgStyle}
`;
const MobileFooter = styled.div`
  ${footerBgStyle}
`;
